import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import QRCode from 'qrcode';
import { scholarshipGrantProgramsContent } from '../content/educational/scholarshipGrantProgramsContent';

// PDF Generation Function
const generatePDF = async (content, title, keywords) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 25;
    const contentWidth = pageWidth - (margin * 2);
    const headerHeight = 35;
    const footerHeight = 15;
    const lineSpacing = 5;
    const paragraphSpacing = 3;

    // Generate QR Codes
    const qrCodeHomepage = await QRCode.toDataURL('https://causeandimpact.org');
    const qrCodeApply = await QRCode.toDataURL('https://causeandimpact.org/apply');

    // Helper function to process markdown text
    const processMarkdown = (text) => {
      // Handle bold text
      text = text.replace(/\*\*(.*?)\*\*/g, (match, content) => {
        doc.setFont('helvetica', 'bold');
        return content;
      });

      // Handle italic text
      text = text.replace(/\*(.*?)\*/g, (match, content) => {
        doc.setFont('helvetica', 'italic');
        return content;
      });

      // Reset font after processing
      doc.setFont('helvetica', 'normal');
      return text;
    };

    // Helper function to handle bullet points and numbered lists
    const processList = (text, yPosition) => {
      const lines = text.split('\n');
      let currentY = yPosition;
      const bulletIndent = 15;
      
      lines.forEach(line => {
        if (line.trim().startsWith('- ')) {
          // Bullet point with increased indentation
          const bulletText = line.substring(2);
          doc.setFont('helvetica', 'normal');
          doc.text('•', margin + bulletIndent, currentY);
          const processedText = processMarkdown(bulletText);
          const bulletLines = doc.splitTextToSize(processedText, contentWidth - (bulletIndent + 10));
          
          bulletLines.forEach((bLine, index) => {
            if (currentY + 6 > pageHeight - footerHeight) {
              doc.addPage();
              addHeader();
              addFooter(doc.internal.getNumberOfPages());
              currentY = headerHeight + 15;
            }
            doc.text(bLine, margin + bulletIndent + 8, currentY);
            currentY += 6;
          });
          currentY += 2;
        } else if (line.match(/^\d+\./)) {
          // Numbered list with matching indentation
          const numberText = line.substring(line.indexOf('.') + 1).trim();
          const processedText = processMarkdown(numberText);
          const numberLines = doc.splitTextToSize(processedText, contentWidth - (bulletIndent + 12));
          
          numberLines.forEach((nLine, index) => {
            if (currentY + 6 > pageHeight - footerHeight) {
              doc.addPage();
              addHeader();
              addFooter(doc.internal.getNumberOfPages());
              currentY = headerHeight + 15;
            }
            if (index === 0) {
              doc.text(line.substring(0, line.indexOf('.') + 1), margin + bulletIndent, currentY);
            }
            doc.text(nLine, margin + bulletIndent + 10, currentY);
            currentY += 6;
          });
          currentY += 2;
        } else {
          // Regular text remains at normal margin
          const processedText = processMarkdown(line);
          const textLines = doc.splitTextToSize(processedText, contentWidth);
          
          textLines.forEach(tLine => {
            if (currentY + 6 > pageHeight - footerHeight) {
              doc.addPage();
              addHeader();
              addFooter(doc.internal.getNumberOfPages());
              currentY = headerHeight + 15;
            }
            doc.text(tLine, margin, currentY);
            currentY += 6;
          });
          currentY += 1;
        }
      });
      
      return currentY;
    };

    const addHeader = () => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, 0, pageWidth, headerHeight, 'F');

      // Gradient title effect
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(24);
      
      // Blue shadow
      doc.setTextColor(37, 99, 235);
      doc.text('Cause & Impact, Inc.', (pageWidth / 2) + 0.5, 20.1, { align: 'center' });
      
      // Green overlay
      doc.setTextColor(5, 150, 105);
      doc.text('Cause & Impact, Inc.', pageWidth / 2, 20, { align: 'center' });

      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.text('Starting a Nonprofit Cause never got easier!', pageWidth / 2, 30, { align: 'center' });

      doc.addImage(qrCodeHomepage, 'PNG', pageWidth - margin - 25, 10, 20, 20);
    };

    const addFooter = (pageNumber) => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(255, 255, 255);
      doc.text(
        'Cause & Impact, Inc. | EIN: 84-3918881 | www.causeandimpact.org',
        pageWidth / 2,
        pageHeight - 8,
        { align: 'center' }
      );
      doc.text(`Page ${pageNumber}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
    };

    const addCallToAction = (yPosition) => {
      const ctaHeight = 45;
      const qrSize = 30;
      doc.setFillColor(37, 99, 235);
      doc.roundedRect(margin, yPosition, contentWidth, ctaHeight, 5, 5, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.text('Ready to Get Started?', pageWidth / 2, yPosition + 20, { align: 'center' });

      doc.setFontSize(12);
      doc.text('Visit causeandimpact.org/apply', pageWidth / 2, yPosition + 30, { align: 'center' });

      doc.addImage(qrCodeApply, 'PNG', pageWidth - qrSize - margin - 10, yPosition + 7.5, qrSize, qrSize);
    };

    const addContent = () => {
      let yPosition = headerHeight + 15;
      let pageNumber = 1;

      // Add title
      doc.setFontSize(28);
      doc.setTextColor(37, 99, 235);
      doc.setFont('helvetica', 'bold');
      const titleLines = doc.splitTextToSize(title, contentWidth);
      doc.text(titleLines, pageWidth / 2, yPosition + 8, { align: 'center' });
      yPosition += (titleLines.length * 8) + 15;

      content.forEach((section, index) => {
        // Check space for new section
        if (yPosition + 40 > pageHeight - footerHeight) {
          doc.addPage();
          pageNumber++;
          addHeader();
          addFooter(pageNumber);
          yPosition = headerHeight + 15;
        }

        // Section Heading
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.setTextColor(5, 150, 105);
        doc.text(section.heading, margin, yPosition);
        yPosition += 10;

        // Process section content with markdown and lists
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(31, 41, 55);
        
        const paragraphs = section.text.split('\n\n');
        paragraphs.forEach(paragraph => {
          yPosition = processList(paragraph, yPosition);
          yPosition += paragraphSpacing;
        });

        // Add CTA if it's the last section
        if (index === content.length - 1) {
          if (yPosition + 45 > pageHeight - footerHeight) {
            doc.addPage();
            pageNumber++;
            addHeader();
            addFooter(pageNumber);
            yPosition = headerHeight + 15;
          }
          addCallToAction(yPosition);
        }
      });
    };

    // Generate PDF
    addHeader();
    addFooter(1);
    addContent();

    return doc;
  } catch (error) {
    console.error('Error generating PDF:', error);
    return null;
  }
};

export const generateScholarshipGrantOverview = async () => {
  return generatePDF(
    scholarshipGrantProgramsContent.sections,
    scholarshipGrantProgramsContent.title,
    scholarshipGrantProgramsContent.keywords
  );
}; 